import { Button, Space, Tooltip, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import BarrierGlyph from '@/components/map/BarrierGlyph';
import CarGlyph from '@/components/map/CarGlyph';
import HandGlyph from '@/components/map/HandGlyph';
import PointerGlyph from '@/components/map/PointerGlyph';
import { useRouteWorkspace } from '@/stores/RouteWorkspaceContext';
import { useMapTool, type MapToolMode } from '@/stores/MapToolContext';

type ToolAction = 'activate_barrier' | 'activate_route';

interface ToolConfig {
  readonly id: string;
  readonly mode: MapToolMode;
  readonly icon: React.ReactNode;
  readonly labelKey: string;
  readonly action?: ToolAction;
}

const TOOLS: readonly ToolConfig[] = [
  { id: 'pan', mode: 'pan', icon: <HandGlyph />, labelKey: 'toolbar.pan' },
  { id: 'select', mode: 'select', icon: <PointerGlyph />, labelKey: 'toolbar.select' },
  {
    id: 'barrier',
    mode: 'create_barrier',
    icon: <BarrierGlyph />,
    labelKey: 'toolbar.barrier',
    action: 'activate_barrier',
  },
  {
    id: 'route',
    mode: 'create_route',
    icon: <CarGlyph />,
    labelKey: 'toolbar.route',
    action: 'activate_route',
  },
];

const ROUTE_MODES: readonly MapToolMode[] = [
  'create_route',
  'pick_route_origin',
  'pick_route_destination',
];

export default function Toolbar() {
  const { t } = useTranslation();
  const { mode, setMode } = useMapTool();
  const { draft, startDraft } = useRouteWorkspace();
  const { token } = theme.useToken();

  const isActiveTool = (tool: ToolConfig): boolean => {
    if (tool.id === 'route') {
      return ROUTE_MODES.includes(mode);
    }
    return mode === tool.mode;
  };

  const activate = (tool: ToolConfig) => {
    if (tool.action === 'activate_route' && draft === null) {
      startDraft();
    }
    setMode(tool.mode);
  };

  return (
    <Space
      direction="vertical"
      size={token.marginSM}
      style={{ padding: token.padding, width: '100%' }}
    >
      {TOOLS.map((tool) => (
        <Tooltip key={tool.id} placement="right" title={t(tool.labelKey)}>
          <Button
            type={isActiveTool(tool) ? 'primary' : 'default'}
            icon={tool.icon}
            size="large"
            onClick={() => activate(tool)}
            aria-label={t(tool.labelKey)}
            aria-pressed={isActiveTool(tool)}
            block
          />
        </Tooltip>
      ))}
    </Space>
  );
}
